/* eslint-disable react/prop-types */
import * as React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import { apipost } from '../../service/api';
import {
  LOAN_TYPES,
  PROPERTY_TYPES,
  PROPERTY_USES,
  RATE_TYPES,
  EMPLOYMENT_STATUSES,
  CREDIT_PROFILES,
} from './statusConfig';

const validationSchema = yup.object({
  leadId: yup.string().required('leadId is required'),
  firstName: yup.string().required('First name is required'),
  lastName: yup.string().required('Last name is required'),
  email: yup.string().email('Invalid email').required('Email is required'),
  phone: yup.string().required('Phone is required'),
  loanType: yup.string().oneOf(LOAN_TYPES).required('Loan type is required'),
});

const initialValues = {
  leadId: '',
  leadSourceCampaign: '',
  leadcost: '',
  productDesc: '',
  productRate: '',
  productAPR: '',
  points: '',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  employmentStatus: '',
  creditProfileDescription: '',
  creditProfileAmount: '',
  grossAnnualIncomeAmount: '',
  loanType: '',
  propertyType: '',
  propertyUse: '',
  zipCode: '',
  propertyCity: '',
  propertyState: '',
  loanAmount: '',
  // purchase
  purchasePriceAmount: '',
  downPaymentAmount: '',
  rateTypeDesired: '',
  isFirstPropertyPurchase: '',
  isWorkingWithRealtor: '',
  // refi
  propertyEstimatedValueAmount: '',
  firstMortgageBalanceAmount: '',
  firstMortgageRate: '',
  additionalCashAmount: '',
  yearHomePurchased: '',
  hasLatePayments: '',
  hasFHALoan: '',
};

const boolOptions = [
  { value: '', label: '—' },
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
];

const AddBankrateLead = ({ open, handleClose, setUserAction }) => {
  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      // Strip empty strings so the server's normalizer drops them.
      const body = Object.fromEntries(
        Object.entries(values).filter(([, v]) => v !== '' && v !== null && v !== undefined)
      );
      const result = await apipost('bankrate-lead/add', body);
      if (result?.status === 201) {
        toast.success('Bankrate lead created');
        formik.resetForm();
        handleClose();
        setUserAction?.(result);
      }
    },
  });

  const isRefi = formik.values.loanType === 'Home Refinance';
  const isPurchase = formik.values.loanType === 'Home Purchase';

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="h6">New Bankrate Lead</Typography>
        <ClearIcon onClick={handleClose} style={{ cursor: 'pointer' }} />
      </DialogTitle>

      <DialogContent dividers>
        <Typography variant="subtitle1" sx={{ mb: 2 }}>Bankrate Metadata</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormLabel>Lead ID *</FormLabel>
            <TextField
              size="small" fullWidth name="leadId"
              value={formik.values.leadId}
              onChange={formik.handleChange}
              error={formik.touched.leadId && Boolean(formik.errors.leadId)}
              helperText={formik.touched.leadId && formik.errors.leadId}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormLabel>Campaign</FormLabel>
            <TextField size="small" fullWidth name="leadSourceCampaign"
              value={formik.values.leadSourceCampaign} onChange={formik.handleChange} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormLabel>Lead Cost ($)</FormLabel>
            <TextField type="number" size="small" fullWidth name="leadcost"
              value={formik.values.leadcost} onChange={formik.handleChange} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormLabel>Product Rate</FormLabel>
            <TextField type="number" size="small" fullWidth name="productRate"
              value={formik.values.productRate} onChange={formik.handleChange} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormLabel>Product APR</FormLabel>
            <TextField type="number" size="small" fullWidth name="productAPR"
              value={formik.values.productAPR} onChange={formik.handleChange} />
          </Grid>
          <Grid item xs={12}>
            <FormLabel>Product Description</FormLabel>
            <TextField size="small" fullWidth name="productDesc"
              value={formik.values.productDesc} onChange={formik.handleChange} />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />
        <Typography variant="subtitle1" sx={{ mb: 2 }}>Borrower</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormLabel>First Name *</FormLabel>
            <TextField size="small" fullWidth name="firstName"
              value={formik.values.firstName} onChange={formik.handleChange}
              error={formik.touched.firstName && Boolean(formik.errors.firstName)}
              helperText={formik.touched.firstName && formik.errors.firstName} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormLabel>Last Name *</FormLabel>
            <TextField size="small" fullWidth name="lastName"
              value={formik.values.lastName} onChange={formik.handleChange}
              error={formik.touched.lastName && Boolean(formik.errors.lastName)}
              helperText={formik.touched.lastName && formik.errors.lastName} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormLabel>Email *</FormLabel>
            <TextField size="small" fullWidth name="email"
              value={formik.values.email} onChange={formik.handleChange}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormLabel>Phone *</FormLabel>
            <TextField size="small" fullWidth name="phone"
              value={formik.values.phone} onChange={formik.handleChange}
              error={formik.touched.phone && Boolean(formik.errors.phone)}
              helperText={formik.touched.phone && formik.errors.phone} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl size="small" fullWidth>
              <InputLabel>Employment Status</InputLabel>
              <Select label="Employment Status" name="employmentStatus"
                value={formik.values.employmentStatus} onChange={formik.handleChange}>
                <MenuItem value="">—</MenuItem>
                {EMPLOYMENT_STATUSES.map((e) => <MenuItem key={e} value={e}>{e}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl size="small" fullWidth>
              <InputLabel>Credit Profile</InputLabel>
              <Select label="Credit Profile" name="creditProfileDescription"
                value={formik.values.creditProfileDescription} onChange={formik.handleChange}>
                <MenuItem value="">—</MenuItem>
                {CREDIT_PROFILES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormLabel>Credit Score (numeric)</FormLabel>
            <TextField type="number" size="small" fullWidth name="creditProfileAmount"
              value={formik.values.creditProfileAmount} onChange={formik.handleChange} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormLabel>Gross Annual Income ($)</FormLabel>
            <TextField type="number" size="small" fullWidth name="grossAnnualIncomeAmount"
              value={formik.values.grossAnnualIncomeAmount} onChange={formik.handleChange} />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />
        <Typography variant="subtitle1" sx={{ mb: 2 }}>Property / Loan</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormControl size="small" fullWidth>
              <InputLabel>Loan Type *</InputLabel>
              <Select label="Loan Type *" name="loanType"
                value={formik.values.loanType} onChange={formik.handleChange}
                error={formik.touched.loanType && Boolean(formik.errors.loanType)}>
                {LOAN_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormLabel>Loan Amount ($)</FormLabel>
            <TextField type="number" size="small" fullWidth name="loanAmount"
              value={formik.values.loanAmount} onChange={formik.handleChange} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl size="small" fullWidth>
              <InputLabel>Property Type</InputLabel>
              <Select label="Property Type" name="propertyType"
                value={formik.values.propertyType} onChange={formik.handleChange}>
                <MenuItem value="">—</MenuItem>
                {PROPERTY_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl size="small" fullWidth>
              <InputLabel>Property Use</InputLabel>
              <Select label="Property Use" name="propertyUse"
                value={formik.values.propertyUse} onChange={formik.handleChange}>
                <MenuItem value="">—</MenuItem>
                {PROPERTY_USES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormLabel>City</FormLabel>
            <TextField size="small" fullWidth name="propertyCity"
              value={formik.values.propertyCity} onChange={formik.handleChange} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormLabel>State</FormLabel>
            <TextField size="small" fullWidth name="propertyState"
              value={formik.values.propertyState} onChange={formik.handleChange} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormLabel>ZIP</FormLabel>
            <TextField size="small" fullWidth name="zipCode"
              value={formik.values.zipCode} onChange={formik.handleChange} />
          </Grid>
        </Grid>

        {isPurchase && (
          <>
            <Divider sx={{ my: 3 }} />
            <Typography variant="subtitle1" sx={{ mb: 2 }}>Purchase Details</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormLabel>Purchase Price ($)</FormLabel>
                <TextField type="number" size="small" fullWidth name="purchasePriceAmount"
                  value={formik.values.purchasePriceAmount} onChange={formik.handleChange} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormLabel>Down Payment ($)</FormLabel>
                <TextField type="number" size="small" fullWidth name="downPaymentAmount"
                  value={formik.values.downPaymentAmount} onChange={formik.handleChange} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Rate Type Desired</InputLabel>
                  <Select label="Rate Type Desired" name="rateTypeDesired"
                    value={formik.values.rateTypeDesired} onChange={formik.handleChange}>
                    <MenuItem value="">—</MenuItem>
                    {RATE_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl size="small" fullWidth>
                  <InputLabel>First-Time Buyer</InputLabel>
                  <Select label="First-Time Buyer" name="isFirstPropertyPurchase"
                    value={formik.values.isFirstPropertyPurchase} onChange={formik.handleChange}>
                    {boolOptions.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Working with Realtor</InputLabel>
                  <Select label="Working with Realtor" name="isWorkingWithRealtor"
                    value={formik.values.isWorkingWithRealtor} onChange={formik.handleChange}>
                    {boolOptions.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </>
        )}

        {isRefi && (
          <>
            <Divider sx={{ my: 3 }} />
            <Typography variant="subtitle1" sx={{ mb: 2 }}>Refinance Details</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormLabel>Estimated Property Value ($)</FormLabel>
                <TextField type="number" size="small" fullWidth name="propertyEstimatedValueAmount"
                  value={formik.values.propertyEstimatedValueAmount} onChange={formik.handleChange} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormLabel>First Mortgage Balance ($)</FormLabel>
                <TextField type="number" size="small" fullWidth name="firstMortgageBalanceAmount"
                  value={formik.values.firstMortgageBalanceAmount} onChange={formik.handleChange} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormLabel>First Mortgage Rate</FormLabel>
                <TextField type="number" size="small" fullWidth name="firstMortgageRate"
                  value={formik.values.firstMortgageRate} onChange={formik.handleChange} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormLabel>Cash Out ($)</FormLabel>
                <TextField type="number" size="small" fullWidth name="additionalCashAmount"
                  value={formik.values.additionalCashAmount} onChange={formik.handleChange} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormLabel>Year Home Purchased</FormLabel>
                <TextField type="number" size="small" fullWidth name="yearHomePurchased"
                  value={formik.values.yearHomePurchased} onChange={formik.handleChange} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Late Payments (12mo)</InputLabel>
                  <Select label="Late Payments (12mo)" name="hasLatePayments"
                    value={formik.values.hasLatePayments} onChange={formik.handleChange}>
                    {boolOptions.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Has FHA Loan</InputLabel>
                  <Select label="Has FHA Loan" name="hasFHALoan"
                    value={formik.values.hasFHALoan} onChange={formik.handleChange}>
                    {boolOptions.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button variant="contained" onClick={formik.handleSubmit}>Save</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddBankrateLead;
